import { useEffect, useId, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { leerMonto, monto } from '../comun/formato';
import { Icono } from './Icono';

// --- Botón ----------------------------------------------------------------------

interface PropsBoton extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: 'primario' | 'secundario' | 'fantasma' | 'peligro';
  tamano?: 'normal' | 'chico' | 'grande';
  icono?: string;
  cargando?: boolean;
}

export function Boton({ variante = 'secundario', tamano = 'normal', icono, cargando, children, className, disabled, ...resto }: PropsBoton) {
  return (
    <button
      type="button"
      className={`boton boton--${variante} boton--${tamano} ${className ?? ''}`}
      disabled={disabled || cargando}
      aria-busy={cargando || undefined}
      {...resto}
    >
      {cargando ? <span className="giro" aria-hidden /> : icono ? <Icono nombre={icono} /> : null}
      {children ? <span>{children}</span> : null}
    </button>
  );
}

// --- Campo: etiqueta, ayuda y error alrededor de cualquier control ------------------

export function Campo({ etiqueta, ayuda, error, children, ancho, id }: { etiqueta: string; ayuda?: ReactNode; error?: string | null; children: (id: string) => ReactNode; ancho?: 'completo' | 'medio' | 'tercio' | 'cuarto'; id?: string }) {
  const generado = useId();
  const idControl = id ?? generado;
  return (
    <div className={`campo ${ancho ? `campo--${ancho}` : ''} ${error ? 'campo--error' : ''}`}>
      <label htmlFor={idControl} className="campo__etiqueta">{etiqueta}</label>
      {children(idControl)}
      {error ? <p className="campo__error" role="alert"><Icono nombre="alerta" />{error}</p> : ayuda ? <p className="campo__ayuda">{ayuda}</p> : null}
    </div>
  );
}

export function Entrada(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`control ${props.className ?? ''}`} />;
}

export function Selector(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="control-select">
      <select {...props} className={`control ${props.className ?? ''}`} />
      <Icono nombre="abajo" />
    </div>
  );
}

export function Area(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea rows={3} {...props} className={`control control--area ${props.className ?? ''}`} />;
}

/**
 * Monto con separador de miles mientras se escribe. El valor es siempre un entero en la
 * unidad mínima: nunca pasa por un float.
 */
export function CampoMonto({ valor, alCambiar, moneda = 'PYG', id, autoFocus, grande, disabled, placeholder }: { valor: number | null; alCambiar: (valor: number | null) => void; moneda?: string; id?: string; autoFocus?: boolean; grande?: boolean; disabled?: boolean; placeholder?: string }) {
  const [texto, setTexto] = useState(valor === null ? '' : monto(valor, moneda, { simbolo: false }));
  useEffect(() => {
    const actual = leerMonto(texto, moneda);
    if (actual !== valor) setTexto(valor === null ? '' : monto(valor, moneda, { simbolo: false }));
    // Solo cuando cambia desde afuera.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor, moneda]);

  return (
    <div className={`control-monto ${grande ? 'control-monto--grande' : ''}`}>
      <span className="control-monto__simbolo">{moneda === 'PYG' ? 'Gs.' : moneda}</span>
      <input
        id={id}
        inputMode="numeric"
        autoComplete="off"
        className="control control--monto"
        value={texto}
        autoFocus={autoFocus}
        disabled={disabled}
        placeholder={placeholder ?? '0'}
        onChange={(evento) => {
          const leido = leerMonto(evento.target.value, moneda);
          setTexto(leido === null ? '' : monto(leido, moneda, { simbolo: false }));
          alCambiar(leido);
        }}
      />
    </div>
  );
}

export function Interruptor({ activo, alCambiar, etiqueta, ayuda, disabled }: { activo: boolean; alCambiar: (valor: boolean) => void; etiqueta: string; ayuda?: string; disabled?: boolean }) {
  return (
    <label className={`interruptor ${disabled ? 'interruptor--deshabilitado' : ''}`}>
      <input type="checkbox" checked={activo} disabled={disabled} onChange={(e) => alCambiar(e.target.checked)} />
      <span className="interruptor__pista" aria-hidden><span className="interruptor__perilla" /></span>
      <span className="interruptor__texto">
        <span>{etiqueta}</span>
        {ayuda ? <small>{ayuda}</small> : null}
      </span>
    </label>
  );
}

/** Opciones cortas lado a lado: ahorran un clic frente a un desplegable. */
export function Segmentado<T extends string>({ opciones, valor, alCambiar, etiqueta }: { opciones: Array<{ valor: T; texto: string; icono?: string; cuenta?: number }>; valor: T; alCambiar: (valor: T) => void; etiqueta: string }) {
  return (
    <div className="segmentado" role="radiogroup" aria-label={etiqueta}>
      {opciones.map((opcion) => (
        <button
          key={opcion.valor}
          type="button"
          role="radio"
          aria-checked={valor === opcion.valor}
          className={`segmentado__opcion ${valor === opcion.valor ? 'segmentado__opcion--activa' : ''}`}
          onClick={() => alCambiar(opcion.valor)}
        >
          {opcion.icono ? <Icono nombre={opcion.icono} /> : null}
          {opcion.texto}
          {opcion.cuenta !== undefined ? <span className="segmentado__cuenta">{opcion.cuenta}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function Buscador({ valor, alCambiar, placeholder, autoFocus }: { valor: string; alCambiar: (valor: string) => void; placeholder: string; autoFocus?: boolean }) {
  return (
    <div className="buscador">
      <Icono nombre="buscar" />
      <input
        type="search"
        className="control buscador__entrada"
        value={valor}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={(e) => alCambiar(e.target.value)}
        aria-label={placeholder}
      />
      {valor ? (
        <button type="button" className="buscador__limpiar" onClick={() => alCambiar('')} aria-label="Limpiar búsqueda">
          <Icono nombre="cerrar" />
        </button>
      ) : null}
    </div>
  );
}

/** Retrasa un valor para no pedirle a la API una búsqueda por tecla. */
export function useDemorado<T>(valor: T, ms = 250): T {
  const [demorado, setDemorado] = useState(valor);
  useEffect(() => {
    const temporizador = setTimeout(() => setDemorado(valor), ms);
    return () => clearTimeout(temporizador);
  }, [valor, ms]);
  return demorado;
}
